import { useAccountStore } from '@/stores/account.js';
import { defineChannelAccountStore } from '@/stores/channelAccount.js';

// The Meta ad account the /meta/* analytics pages read. Unlike the other channels, the thing olap
// reads is not a connection but ONE ad account of it: the `Metaadaccountid` header holds the
// integration service's `meta_integrated_resource.id` (the `id` of the resources API), and olap
// resolves the bare ad account id and its currency from it, so a page shows one ad account and one
// currency. The ad account id and name are display only and never travel.
//
// Only readable ad accounts are offered, the same rule olap's read gate applies: an ACTIVE
// connection (a token that is valid, not DISCONNECTED / REVOKED / BLOCKED / INACTIVE) and an ad
// account that is ticked (`isConnected`) and `ACCESSIBLE`. A connection that is not ACTIVE has no
// access to channel data. With none, the pages show the empty state that points at Settings.
export const isReadableMetaConnection = (connection) => connection?.status === 'ACTIVE';

export const isReadableMetaAdAccount = (resource) =>
  !!resource?.isConnected && resource?.status === 'ACCESSIBLE';

// The resources API pages with a cursor, at most 500 rows a page. A guard against a cursor that
// never ends; an account has nowhere near this many ticked ad accounts.
const PAGE_SIZE = 500;
const MAX_PAGES = 20;

const fetchConnectedResources = async (account, connection) => {
  const rows = [];
  let cursor = null;
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const { items, nextCursor } = await account.fetchMetaResources(connection.id, {
      connected: true,
      limit: PAGE_SIZE,
      ...(cursor ? { cursor } : {}),
    });
    rows.push(...items);
    if (!nextCursor) break;
    cursor = nextCursor;
  }
  return rows
    .filter(isReadableMetaAdAccount)
    .map((resource) => ({
      id: resource.id,
      adAccountId: resource.adAccountId != null ? String(resource.adAccountId) : null,
      name: resource.name ?? null,
      currency: resource.currency ?? null,
      timezoneName: resource.timezoneName ?? null,
      status: resource.status,
      isConnected: true,
      connectionId: connection.id,
      businessName: connection.businessName ?? null,
    }));
};

// Every readable ad account of the business profile: its ACTIVE connections x their ticked,
// ACCESSIBLE ad accounts, oldest connection first (the order the connections API returns).
export const fetchReadableMetaAdAccounts = async () => {
  const account = useAccountStore();
  const connections = ((await account.fetchMetaConnections()) || []).filter(
    isReadableMetaConnection,
  );
  const perConnection = await Promise.all(
    connections.map((connection) => fetchConnectedResources(account, connection)),
  );
  return perConnection.flat();
};

// Rows are all readable, so none carries the ACTIVE status the shared picker prefers; it then
// falls back to the first row, which is a readable ad account too.
export const useMetaAdAccountStore = defineChannelAccountStore('metaAdAccount', {
  storagePrefix: 'wp.meta.selectedAdAccount',
  fetchAccounts: fetchReadableMetaAdAccounts,
});

// An ad account's display name: its own name, else the ad account id, else the resource id.
export const metaAdAccountLabel = (adAccount) => {
  const name = adAccount?.name || adAccount?.adAccountId || (adAccount?.id != null ? `#${adAccount.id}` : '');
  return adAccount?.currency && name ? `${name} · ${adAccount.currency}` : name;
};
